import React from "react";
import {
  Row,
  Col,
  Typography,
  Divider,
  Space,
  Alert,
  Badge,
  Tag,
  Collapse,
  Table,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  getRiskColor,
  getRiskLevelDisplay,
} from "../helpers/utilities";

const { Text } = Typography;
const { Panel } = Collapse;

interface RiskProfile {
  finalScore: number;
  riskLevel: string;
  factorsEvaluated: number;
  totalPossibleFactors: number;
  details: any;
  missingDetails: any;
  computedAt: string;
}

const RiskProfileDisplay: React.FC<{
  riskProfile?: RiskProfile;
}> = ({ riskProfile }) => {
  if (!riskProfile) return null;

  const getRiskIcon = (level: string) => {
    switch (level) {
      case "low_risk":
        return <CheckCircleOutlined />;
      case "medium_risk":
        return <WarningOutlined />;
      case "high_risk":
        return <ExclamationCircleOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const detailsData = riskProfile.details
    ? Object.entries(riskProfile.details).map(
        ([key, value]: [string, any]) => ({
          key,
          factor: key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          selection: value?.selection || "N/A",
          rating: value?.rating || "N/A",
          score: value?.score || "N/A",
          category: value?.category || "N/A",
        }),
      )
    : [];

  const missingDetailsData = riskProfile.missingDetails
    ? Object.entries(riskProfile.missingDetails).map(
        ([key, value]: [string, any]) => ({
          key,
          factor: key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          reason: value?.reason || "Not provided",
          category: value?.category || "N/A",
        }),
      )
    : [];

  const completionPercentage =
    riskProfile.totalPossibleFactors > 0
      ? Math.round(
          (riskProfile.factorsEvaluated / riskProfile.totalPossibleFactors) *
            100,
        )
      : 0;

  return (
      <Col span={24}>
        <Collapse style={{ marginBottom: 24 }}>
          <Panel
            header={
              <Space>
                <Text>Risk Assessment Profile</Text>
                <Tag color={getRiskColor(riskProfile.riskLevel)}>
                  {getRiskLevelDisplay(riskProfile.riskLevel)}
                </Tag>
              </Space>
            }
            key="risk-assessment"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Alert
                  message={
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Space>
                        <Text strong>Risk Level:</Text>
                        <Badge
                          status={getRiskColor(riskProfile.riskLevel) as any}
                          text={
                            <Text strong style={{ fontSize: 16 }}>
                              {getRiskLevelDisplay(riskProfile.riskLevel)}
                            </Text>
                          }
                        />
                      </Space>
                      <Space split={<Divider type="vertical" />}>
                        <div>
                          <Text strong>Final Score: </Text>
                          <Text>{riskProfile.finalScore.toFixed(2)}</Text>
                        </div>
                        <div>
                          <Text strong>Factors Evaluated: </Text>
                          <Text>
                            {riskProfile.factorsEvaluated} /{" "}
                            {riskProfile.totalPossibleFactors}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Completion: </Text>
                          <Text>{completionPercentage}%</Text>
                        </div>
                      </Space>
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        Computed on{" "}
                        {moment(riskProfile.computedAt).format(
                          "MMMM D, YYYY [at] h:mm A",
                        )}
                      </Text>
                    </div>
                  }
                  type={getRiskColor(riskProfile.riskLevel) as any}
                  showIcon
                  icon={getRiskIcon(riskProfile.riskLevel)}
                />
              </Col>

              <Col span={24}>
                <Collapse style={{ marginTop: 16 }}>
                  <Panel
                    header={
                      <Space>
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        <Text strong>
                          Evaluated Risk Factors ({riskProfile.factorsEvaluated}
                          )
                        </Text>
                      </Space>
                    }
                    key="evaluated"
                  >
                    {detailsData.length > 0 ? (
                      <Table
                        dataSource={detailsData}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: "Factor",
                            dataIndex: "factor",
                            key: "factor",
                            width: "25%",
                          },
                          {
                            title: "Selection",
                            dataIndex: "selection",
                            key: "selection",
                            width: "20%",
                            render: (selection: any) => (
                              <Text>{formatSnakeCase(selection)}</Text>
                            ),
                          },
                          {
                            title: "Rating",
                            dataIndex: "rating",
                            key: "rating",
                            width: "15%",
                            render: (rating: string) => {
                              const color =
                                rating === "low"
                                  ? "green"
                                  : rating === "medium"
                                  ? "orange"
                                  : rating === "high"
                                  ? "red"
                                  : "default";
                              return (
                                <Tag color={color}>
                                  {rating
                                    ? capitalizeFirstLetter(rating)
                                    : "N/A"}
                                </Tag>
                              );
                            },
                          },
                          {
                            title: "Score",
                            dataIndex: "score",
                            key: "score",
                            width: "10%",
                            render: (score: any) => (
                              <Tag color="blue">
                                {typeof score === "number" ? score : score}
                              </Tag>
                            ),
                          },
                          {
                            title: "Category",
                            dataIndex: "category",
                            key: "category",
                            width: "30%",
                            render: (category: string) => (
                              <Text type="secondary">
                                {formatSnakeCase(category)}
                              </Text>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <Text type="secondary">
                        No evaluated factors available
                      </Text>
                    )}
                  </Panel>

                  {missingDetailsData.length > 0 && (
                    <Panel
                      header={
                        <Space>
                          <InfoCircleOutlined style={{ color: "#faad14" }} />
                          <Text strong>
                            Missing Factors ({missingDetailsData.length})
                          </Text>
                        </Space>
                      }
                      key="missing"
                    >
                      <Table
                        dataSource={missingDetailsData}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: "Factor",
                            dataIndex: "factor",
                            key: "factor",
                            width: "30%",
                          },
                          {
                            title: "Category",
                            dataIndex: "category",
                            key: "category",
                            width: "30%",
                            render: (category: string) => (
                              <Text type="secondary">
                                {formatSnakeCase(category)}
                              </Text>
                            ),
                          },
                          {
                            title: "Reason Not Evaluated",
                            dataIndex: "reason",
                            key: "reason",
                            width: "40%",
                            render: (reason: string) => (
                              <Text type="secondary">
                                {formatSnakeCase(reason)}
                              </Text>
                            ),
                          },
                        ]}
                      />
                    </Panel>
                  )}
                </Collapse>
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </Col>
  );
};

export { RiskProfileDisplay };
